import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { ScrollToFirstError } from './ScrollToFirstError';
import * as Yup from 'yup';
import { showToast } from '@klpkonsern/ui';

import { useExecuteLeverandorTaskMutation } from '@klp/leverandor/services/rtkApi';
import { LeverandorRoutes } from '@klp/leverandor/config';
import { CustomFormField } from './FormField';
import type { Aktivitet, FormField } from '../interfaces';
import {
    findCamundaPropertyValue,
    findCamundaValidation,
    formfieldIsHidden,
    withTextMaxLength,
} from '../utils';
import { FormContainerFieldGodkjenn } from './FormContainerFieldGodkjenn';
import { FormContainerFieldComplete } from './FormContainerFieldComplete';
import { AutoSaveLeverandorFormfields } from './AutoSaveLeverandorForm';
import { getFormFieldStyles } from '../formFieldStyles';
import { useFormContext } from '../context/FormContext';

const ASSIGNMENT_ERROR_REGEX =
    /Oppgave\/task Id \(([^)]+)\) is assigned to \(([^)]+)\) not to user \(([^)]+)\)/;

export const createValidationSchema = (
    formFields: Array<FormField>,
    values: FormValuesType,
    regionContext?: string,
    t?: (key: string, options?: Record<string, unknown>) => string,
    // Autosave validates without required rules so drafts can still be saved
    options?: { skipRequired?: boolean },
) => {
    const schemaFields: { [fieldId: string]: Yup.AnySchema } = {};

    formFields.forEach((field) => {
        if (!field.id) return;

        const validation = field.validation || [];
        const properties = field.properties || [];

        const isReadOnly = Boolean(
            findCamundaValidation('readonly', validation),
        );
        const subtype = findCamundaPropertyValue('type', properties);
        const isHidden = formfieldIsHidden(field, values, regionContext);

        if (isReadOnly || subtype === 'constant' || isHidden) {
            return;
        }

        const required = findCamundaValidation('required', validation);
        const shouldBeRequired = required && !options?.skipRequired;
        if (field.type === 'string') {
            let validator = Yup.string();

            if (shouldBeRequired) {
                validator = validator.required(
                    t?.('formGenerator.fieldRequired') ??
                        'This field is required',
                );
            }

            validator = withTextMaxLength(validator, subtype, t);

            schemaFields[field.id] = validator;
        }

        if (field.type === 'long') {
            let validator = Yup.number();

            if (shouldBeRequired) {
                validator = validator.required(
                    t?.('formGenerator.fieldRequired') ??
                        'This field is required',
                );
            }

            schemaFields[field.id] = validator;
        }

        if (field.type === 'date') {
            let validator = Yup.date();

            if (shouldBeRequired) {
                validator = validator.required(
                    t?.('formGenerator.fieldRequired') ??
                        'This field is required',
                );
            }

            schemaFields[field.id] = validator;
        }

        if (field.type === 'boolean') {
            let validator = Yup.boolean().nullable();

            if (shouldBeRequired) {
                validator = validator.required(
                    t?.('formGenerator.fieldRequired') ??
                        'This field is required',
                );
            }

            schemaFields[field.id] = validator;
        }

        if (field.type === 'enum') {
            let validator = Yup.string();

            if (shouldBeRequired) {
                validator = validator.required(
                    t?.('formGenerator.fieldRequired') ??
                        'This field is required',
                );
            }

            schemaFields[field.id] = validator;
        }
    });

    return Yup.object().shape(schemaFields);
};

export type FormValuesType = { [id: string]: string };

type FormContainerProps = {
    prosessInstansId: string;
    activity: Aktivitet;
    formFields: Array<FormField>;
    isDisabled: boolean;
    superofficeId: string;
    orgnummer: string;
};

export const FormContainerLeverandor = ({
    prosessInstansId,
    activity,
    formFields,
    isDisabled,
    superofficeId,
    orgnummer,
}: FormContainerProps) => {
    const { t, i18n } = useTranslation();
    const [executeLeverandorTask, { isLoading }] =
        useExecuteLeverandorTaskMutation();
    const [approvalStatus, setApprovalStatus] = useState('');
    const { regionContext } = useFormContext();
    const history = useHistory();

    const formFieldClasses = getFormFieldStyles();

    const initialValues = useMemo(
        () =>
            Object.fromEntries(
                formFields.map((field) => [field.id, field.value ?? '']),
            ),
        [formFields],
    );

    // Memoize the validation schema to prevent recreation on every render
    const validationSchema = useMemo(
        () =>
            Yup.lazy((values: FormValuesType) =>
                createValidationSchema(formFields, values, regionContext, t),
            ),
        [formFields, regionContext, t],
    );

    const autosaveValidationSchema = useMemo(
        () =>
            Yup.lazy((values: FormValuesType) =>
                createValidationSchema(formFields, values, regionContext, t, {
                    skipRequired: true,
                }),
            ),
        [formFields, regionContext, t],
    );

    const isFieldTypeGodkjenn = useMemo(
        () => formFields.find((f) => f.id.includes('Godkjent'))?.id,
        [formFields],
    );

    // Group form fields by layoutRow
    const groupedFormFields = useMemo(() => {
        const groups: Array<{
            layoutRow: string | null;
            fields: Array<FormField>;
        }> = [];
        const layoutRowMap = new Map<string | null, Array<FormField>>();

        formFields.forEach((field) => {
            const layoutRow =
                field.properties?.find((prop) => prop.id === 'layoutRow')
                    ?.value ?? null;

            let fieldsArray = layoutRowMap.get(layoutRow);

            if (!fieldsArray) {
                fieldsArray = [];
                layoutRowMap.set(layoutRow, fieldsArray);
                groups.push({
                    layoutRow,
                    fields: fieldsArray,
                });
            }

            fieldsArray.push(field);
        });

        return groups;
    }, [formFields]);

    const handleSubmit = useCallback(
        (values: FormValuesType) => {
            if (!activity.id) return;

            const updatedFormFields: Array<FormField> = formFields.map(
                (field) => {
                    const value =
                        isFieldTypeGodkjenn === field.id
                            ? approvalStatus
                            : values[field.id];

                    return { ...field, value };
                },
            );

            executeLeverandorTask({
                oppgaveId: activity.id,
                formFields: updatedFormFields,
            })
                .unwrap()
                .then(() => {
                    showToast({
                        body: t('prosjekt.successfulOperation'),
                        type: 'success',
                    });
                    history.push(LeverandorRoutes.leverandorSchema());
                })
                .catch((e) => {
                    const match = e?.data?.match(ASSIGNMENT_ERROR_REGEX);
                    showToast({
                        body: match
                            ? t('prosjekt.notAssignedToUser')
                            : t('general.error.message'),
                        type: 'error',
                    });
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            activity.id,
            formFields,
            isFieldTypeGodkjenn,
            approvalStatus,
            executeLeverandorTask,
            history,
        ],
    );

    return (
        <Formik
            name="formContainer"
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize={true}
        >
            {({ values, isSubmitting }) => (
                <Form noValidate={true}>
                    <ScrollToFirstError formFields={formFields} />
                    <AutoSaveLeverandorFormfields
                        validationSchema={autosaveValidationSchema}
                        fields={formFields}
                        activityId={activity.id}
                        isDisabled={isDisabled || isSubmitting}
                    />
                    {groupedFormFields.map((group, groupIndex) => {
                        if (group.fields.length === 1) {
                            const field = group.fields[0];
                            return (
                                <CustomFormField
                                    containerModule="leverandor"
                                    key={field.id}
                                    prosessInstansId={prosessInstansId}
                                    field={field}
                                    fields={formFields}
                                    formIsDisabled={isDisabled}
                                    locale={i18n.language}
                                    hiddenField={formfieldIsHidden(
                                        field,
                                        values,
                                        regionContext,
                                    )}
                                    superofficeContactId={superofficeId}
                                    organisasjonsnummer={orgnummer}
                                />
                            );
                        }

                        return (
                            <div
                                key={group.layoutRow ?? groupIndex}
                                className={formFieldClasses.fieldsTwoColumns}
                            >
                                {group.fields.map((field) => (
                                    <CustomFormField
                                        containerModule="leverandor"
                                        key={field.id}
                                        prosessInstansId={prosessInstansId}
                                        field={field}
                                        fields={formFields}
                                        formIsDisabled={isDisabled}
                                        locale={i18n.language}
                                        hiddenField={formfieldIsHidden(
                                            field,
                                            values,
                                            regionContext,
                                        )}
                                        superofficeContactId={superofficeId}
                                        organisasjonsnummer={orgnummer}
                                    />
                                ))}
                            </div>
                        );
                    })}

                    {isFieldTypeGodkjenn && (
                        <FormContainerFieldGodkjenn
                            isDisabled={isDisabled}
                            isLoading={isLoading}
                            setApprovalStatus={setApprovalStatus}
                            onSubmit={handleSubmit}
                        />
                    )}
                    {!isFieldTypeGodkjenn && (
                        <>
                            <FormContainerFieldComplete
                                isDisabled={isDisabled}
                                isLoading={isLoading}
                                activity={activity}
                            />
                        </>
                    )}
                </Form>
            )}
        </Formik>
    );
};
